import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
  VisuallyHidden,
  useDisclosure,
  Stack,
  Text,
} from '@chakra-ui/react';
import { withTranslation } from 'next-i18next';
import Cookies from 'js-cookie';
import HTMLWrapper from 'lib/html-wrapper';
import { TFunction, WithTranslation } from 'next-i18next';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import CheckCircle from 'static/assets/images/check-circle.png';
import css from 'styled-jsx/css';
import { removeGACookies } from './helper';

// interface CookieConsent extends WithTranslation {
//   t: TFunction;
//   isGoogleAnalyticsEnabled: boolean;
//   isUserTrackingAnalyticsEnabled: boolean;
//   brandColor: string;
// }

const { className, styles } = css.resolve`
  .disabled-image {
    height: 32px;
  }
  .modal-content {
    color: black;
    background-color: white !important;
  }
  .secondary-button {
    font-weight: 500;
    background-color: white;
    color: #595959;
    border: 1px solid #d9d9d9;
  }
  .confirm-button {
    font-weight: 500;
    color: white;
  }
  .cookie-consent {
    height: auto;
    width: 100%;
    bottom: 0px;
    position: fixed;
    background-color: white;
    padding: 2rem;
    box-shadow: 0px -4px 12px rgba(0, 0, 0, 0.15);
    z-index: 999;
  }
  .manage-cookies {
    font-size: 16px;
    font-weight: bold;
    color: #262626;
  }
`;

const CookieConsentModal = ({
  t,
  isGoogleAnalyticsEnabled,
  isUserTrackingAnalyticsEnabled,
  brandColor,
}) => {
  const [showManageAllContent, setShowManageAllContent] = useState(false);
  const [showDrawer, setShowDrawer] = useState(true);
  const analyticsStatus =
    isGoogleAnalyticsEnabled || isUserTrackingAnalyticsEnabled;
  const [isAnalyticsCookieEnabled, setAnalyticsCookie] = useState(false);

  const cookiesList = [
    { name: 'essential', status: true },
    { name: 'analytics', status: isAnalyticsCookieEnabled },
  ];

  const setCookieConsent = () => {
    // for setting the cookie consent cookie and closing the modal
    const currentDate = new Date();

    // Creating a expiry date for the cookie which is exactly after two years
    currentDate.setMonth(currentDate.getMonth() + 24);

    const cookieObject = {
      functional: true,
      analytics: isAnalyticsCookieEnabled,
    };

    // no http-only setting as it won't be able to access it from the client side
    Cookies.set('cookie_consent', cookieObject, {
      expires: currentDate, // Ensure currentDate is a Date object
      path: '/',
      secure: true, // Cookie will only be sent over HTTPS
      sameSite: 'strict', // Prevents the cookie from being sent in cross-site requests
    });

    const setGoogleAnalyticsStorage =
      (isUserTrackingAnalyticsEnabled || isGoogleAnalyticsEnabled) &&
      isAnalyticsCookieEnabled;
    if (setGoogleAnalyticsStorage) {
      // gtag -> is from the third party google analytics
      if (gtag) {
        gtag('consent', 'update', { analytics_storage: 'granted' });
      }
    }
    setShowDrawer(false);
    setShowManageAllContent(false);
  };

  const handleChange = (cookieName, value) => {
    if (cookieName === 'analytics') {
      setAnalyticsCookie(value);
    }
  };

  const renderCookieReview = () => {
    return (
      <p className={`${className} ehq-mt-4 cookie-review`}>
        {t('common:common.analyticsCookieReview')}
      </p>
    );
  };

  const isCookieDisabled = (cookieName) => {
    if (cookieName === 'analytics') return analyticsStatus;
    return true;
  };

  const renderToggle = (cookie) => {
    if (cookie.name === 'essential') {
      return (
        <>
          <img
            alt="check-circle"
            className={`${className} disabled-image`}
            src={CheckCircle}
            aria-hidden="true"
          />
          <VisuallyHidden>
            {t(`common:common.cookies.essentialCookies`)}
          </VisuallyHidden>
        </>
      );
    }

    const handleKeyPress = (event) => {
      if (event.key === 'Enter') {
        handleChange(cookie.name, !cookie.status);
      }
    };
    return (
      <>
        <VisuallyHidden>
          {t(`common:common.cookies.${cookie.name}Cookies`)}
        </VisuallyHidden>

        <Switch
          isChecked={cookie.status}
          onKeyDown={(event) => handleKeyPress(event)}
          isDisabled={!isCookieDisabled(cookie.name)}
          aria-label={t(`common:common.cookies.${cookie.name}Cookies`)}
          onChange={(e) => handleChange(cookie.name, e.target.checked)}
        />
      </>
    );
  };

  const renderCookies = () => {
    const activeCookieClass = 'ehq-text-gray-800';
    const inactiveCookieClass = 'ehq-text-gray-500 ehq-select-none';

    return cookiesList.map((cookie, index) => {
      const cookieListClass = isCookieDisabled(cookie.name)
        ? activeCookieClass
        : inactiveCookieClass;
      return (
        <div
          key={index}
          className={`ehq-pt-4 ${cookieListClass}`}
          title={`${
            cookie.status ? '' : t('common:common.cookies.disbledTooltip')
          }`}
        >
          <div className="ehq-flex ehq-items-center ehq-justify-between">
            <Box className="ehq-w-4/5">
              <p className="ehq-font-bold">
                {t(`common:common.cookies.${cookie.name}Cookies`)}
              </p>
              <p>
                {t(`common:common.cookies.${cookie.name}CookiesDescription`)}
              </p>
            </Box>

            <Box>{renderToggle(cookie)}</Box>
          </div>
          <hr className="ehq-mt-4" />
        </div>
      );
    });
  };

  const handleSwitchModalAction = () => {
    setShowDrawer(!showDrawer);
    setShowManageAllContent(!showManageAllContent);
  };

  useEffect(() => {
    const cookieConsent = Cookies.get('cookie_consent');
    const isAnalyticsAccessGiven =
      cookieConsent && JSON.parse(cookieConsent)?.analytics;

    const isAnalyticsCookieDenied = !isAnalyticsAccessGiven;
    if (isAnalyticsCookieDenied) {
      removeGACookies();
    }
  }, []);

  // Modal state
  const { onClose } = useDisclosure();

  const handleAcceptAll = () => {
    // for setting the cookie consent cookie and closing the modal
    const currentDate = new Date();

    // Creating a expiry date for the cookie which is exactly after two years
    currentDate.setMonth(currentDate.getMonth() + 24);

    const cookieObject = {
      functional: true,
      analytics: true,
    };

    // no http-only setting as it won't be able to access it from the client side
    Cookies.set('cookie_consent', cookieObject, {
      expires: currentDate, // Ensure currentDate is a Date object
      path: '/',
      secure: true, // Cookie will only be sent over HTTPS
      sameSite: 'strict', // Prevents the cookie from being sent in cross-site requests
    });

    if (isUserTrackingAnalyticsEnabled || isGoogleAnalyticsEnabled) {
      // gtag -> is from the third party google analytics
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('consent', 'update', { analytics_storage: 'granted' });
      }
    }

    setShowDrawer(false);
    setShowManageAllContent(false);
  };

  const getManageCookieModal = () => {
    return (
      <Modal
        isOpen={showManageAllContent}
        closeOnOverlayClick={false}
        blockScrollOnMount
        size="xl"
        onClose={handleSwitchModalAction}
      >
        <ModalOverlay />
        <ModalContent className={`${className} modal-content`}>
          <ModalHeader>
            <h2>{t('common:common.cookies.manageCookies')}</h2>
          </ModalHeader>
          <ModalBody>
            <HTMLWrapper
              html={t('common:common.cookies.manageCookiesDescription')}
            />
            {renderCookies()}
            {!analyticsStatus && renderCookieReview()}
          </ModalBody>
          <ModalFooter justifyContent="flex-start">
            <Button
              backgroundColor={brandColor}
              className={`${className} confirm-button ehq-mr-3`}
              onClick={setCookieConsent}
            >
              {t('common:common.cookies.save&Close')}
            </Button>
            <Button
              key="cancel"
              className={`${className} secondary-button`}
              onClick={handleSwitchModalAction}
            >
              {t('common:common.cancel')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  };

  return (
    <>
      {getManageCookieModal()}
      <Drawer
        placement={'bottom'}
        isOpen={showDrawer}
        closeOnOverlayClick={false}
        size="lg"
        onClose={onClose}
      >
        <DrawerOverlay />
        <DrawerContent className={`${className} cookie-consent`}>
          <DrawerHeader p={0}>
            <h2 className={`${className} manage-cookies`}>
              {t('common:common.cookies.descriptionOne')}
            </h2>
          </DrawerHeader>
          <DrawerBody p={0}>
            <HTMLWrapper html={t('common:common.cookies.newDescriptionTwo')} />
            <div className="ehq-pt-8">
              <Stack direction={['column', 'column', 'row']} spacing={2}>
                <Button
                  backgroundColor={brandColor}
                  mr={3}
                  onClick={setCookieConsent}
                  className={`${className} confirm-button ehq-mr-0`}
                >
                  {t('common:common.cookies.acceptEssential')}
                </Button>

                <Button
                  backgroundColor={brandColor}
                  mr={3}
                  onClick={handleAcceptAll}
                  className={`${className} confirm-button`}
                >
                  {t('common:common.cookies.acceptAll')}
                </Button>

                <Button
                  className={`${className} secondary-button`}
                  onClick={handleSwitchModalAction}
                >
                  {t('common.cookies.manageCookies')}
                </Button>
              </Stack>
            </div>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
      {styles}
    </>
  );
};

CookieConsentModal.propTypes = {
  t: PropTypes.func.isRequired,
  isGoogleAnalyticsEnabled: PropTypes.bool,
  isUserTrackingAnalyticsEnabled: PropTypes.bool,
};

export default withTranslation(['common'])(CookieConsentModal);
